import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';

interface PageLoaderProps {
  children: React.ReactNode;
  loading?: boolean;
}

/**
 * PageLoader component that shows a full-page loader during navigation
 * and only displays content when loading is complete.
 */
export const PageLoader: React.FC<PageLoaderProps> = ({ children, loading = false }) => {
  const location = useLocation();
  const [isNavigating, setIsNavigating] = useState(true); // Start as true to show loader on initial mount
  const [currentPath, setCurrentPath] = useState<string | null>(null);

  useEffect(() => {
    // When pathname changes, show loader
    if (currentPath === null) {
      // Initial mount - set current path but keep loading until data loads
      setCurrentPath(location.pathname);
    } else if (location.pathname !== currentPath) {
      // Navigation to new page
      setIsNavigating(true);
      setCurrentPath(location.pathname);
    }
  }, [location.pathname, currentPath]);

  useEffect(() => {
    // Hide navigation loader when page loading is complete
    if (!loading && isNavigating) {
      // Small delay to ensure smooth transition
      const timer = setTimeout(() => {
        setIsNavigating(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, isNavigating]);

  // Show loader if navigating or page is loading
  if (isNavigating || loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  return <>{children}</>;
};

